// File: /opt/dev_environment/assets/workspace/app-init/src/components/CompartmentVisualization3D/CompartmentVisualization3D.js

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { LEITNER_COMPARTMENTS } from '../../constants/leitner';
import './CompartmentVisualization3D.css';

const MAX_PILE_HEIGHT = 85;
const MIN_PILE_HEIGHT = 6;
const ANIMATION_DURATION = 500;

const PILE_SCALES = [1.0, 0.88, 0.76, 0.66, 0.57];
const PILE_Y_OFFSETS = [0, -5, -9, -13, -16];
const PILE_LEFT_PERCENTS = [2, 22, 40, 56, 70];
const PILE_WIDTHS = [18, 16, 14, 12, 11];

function CompartmentVisualization3D({ counts }) {
  const totalCards = useMemo(() => {
    return Object.values(counts).reduce((sum, c) => sum + c, 0);
  }, [counts]);

  const hasAnyCards = totalCards > 0;

  const pileHeights = useMemo(() => {
    const heights = {};
    for (let i = 1; i <= 5; i++) {
      if (totalCards === 0) {
        heights[i] = MIN_PILE_HEIGHT;
      } else {
        const proportion = counts[i] / totalCards;
        const height = proportion * MAX_PILE_HEIGHT;
        heights[i] = Math.max(height, MIN_PILE_HEIGHT);
      }
    }
    return heights;
  }, [counts, totalCards]);

  const prevHeightsRef = useRef(pileHeights);
  const [animatedHeights, setAnimatedHeights] = useState(pileHeights);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const startHeights = { ...prevHeightsRef.current };
    const endHeights = { ...pileHeights };
    const startTime = performance.now();

    function animate(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      const interpolated = {};
      for (let i = 1; i <= 5; i++) {
        const start = startHeights[i] || MIN_PILE_HEIGHT;
        const end = endHeights[i] || MIN_PILE_HEIGHT;
        interpolated[i] = start + (end - start) * eased;
      }

      setAnimatedHeights(interpolated);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        prevHeightsRef.current = endHeights;
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [pileHeights]);

  return (
    <div
      className="compartment-viz-3d"
      role="img"
      aria-label="3D-Darstellung der Leitner-Fächer-Verteilung"
    >
      <div className="compartment-viz-3d__piles-area">
        {[5, 4, 3, 2, 1].map((num) => {
          const idx = num - 1;
          const height = animatedHeights[num] || MIN_PILE_HEIGHT;
          const isEmpty = counts[num] === 0;
          const config = LEITNER_COMPARTMENTS[num];
          const scale = PILE_SCALES[idx];
          const yOffset = PILE_Y_OFFSETS[idx];
          const leftPct = PILE_LEFT_PERCENTS[idx];
          const widthPct = PILE_WIDTHS[idx];
          const scaledHeight = height * scale;
          const lineCount = !isEmpty && scaledHeight > 14
            ? Math.min(Math.floor(scaledHeight / 7), 12)
            : 0;

          return (
            <div
              key={num}
              className="compartment-viz-3d__pile-position"
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                bottom: `${12 + yOffset * -0.5}%`,
                transform: `translateY(${yOffset}px)`,
                zIndex: 6 - num,
              }}
              aria-label={`${config.label}: ${counts[num] || 0} Karten`}
            >
              <div
                className="compartment-viz-3d__pile-shadow"
                style={{
                  opacity: isEmpty ? 0.08 : 0.18,
                  transform: `scaleX(${0.9 + (scaledHeight / MAX_PILE_HEIGHT) * 0.3})`,
                }}
              />

              <div
                className={`compartment-viz-3d__pile compartment-viz-3d__pile--${num} ${isEmpty ? 'compartment-viz-3d__pile--empty' : ''}`}
                style={{ '--pile-height': `${scaledHeight}px` }}
              >
                <div className="compartment-viz-3d__pile-front">
                  {lineCount > 0 && (
                    <div className="compartment-viz-3d__pile-lines">
                      {Array.from({ length: lineCount }).map((_, i) => (
                        <div key={i} className="compartment-viz-3d__pile-line" />
                      ))}
                    </div>
                  )}
                </div>
                <div className="compartment-viz-3d__pile-top" />
                <div className="compartment-viz-3d__pile-side" />
              </div>

              <div
                className="compartment-viz-3d__pile-label"
                style={{ fontSize: `${Math.max(scale * 0.6, 0.4)}rem` }}
              >
                {num}
              </div>
            </div>
          );
        })}
      </div>

      {!hasAnyCards && (
        <div className="compartment-viz-3d__empty-hint">
          Füge Vokabeln hinzu, um deine Fächer zu füllen
        </div>
      )}
    </div>
  );
}

export default CompartmentVisualization3D;